import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./about.css";

const VALUES = [
  {
    title: "Customized engineering",
    text: "Every unit is designed around the application — simultaneous control of temperature, humidity and CO₂ for cold stores and industrial processes.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
      </svg>
    ),
  },
  {
    title: "Eco-friendly refrigerants",
    text: "R410a and natural refrigerants such as ammonia, running on scroll, reciprocating and screw compressors.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
      </svg>
    ),
  },
  {
    title: "In-house manufacturing",
    text: "Mechanical, electrical and electronics systems built end-to-end at our 30,000 ft² factory in RIICO Industrial Area, Neemrana, Rajasthan.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
      </svg>
    ),
  },
  {
    title: "Installations worldwide",
    text: "Our machines run across India and in several other countries — from industrial plants to island resorts.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
      </svg>
    ),
  },
];

const PRODUCTS = [
  { name: "Gas Liquefaction", desc: "CO₂, N₂O and CO₂ separation from biogas" },
  { name: "Cold Stores, Blast Freezers & Chillers", desc: "For fruits & vegetables, meat & poultry and more" },
  { name: "Mushroom Farm Climate Control", desc: "Growing rooms and tunnels" },
  { name: "Ice Machines", desc: "Tube ice from 5 TPD, flake ice from 1 TPD" },
  { name: "Process Chillers", desc: "For exo-gas, milk, water and glycol" },
  { name: "Heat Pumps", desc: "Hot water for swimming pools, hostels and hotels" },
];

function About() {
  const navigate = useNavigate();

  return (
    <div>
      <Navbar />

      <main className="page-shell">
        <section className="about-hero">
          <span className="about-overline">About us</span>
          <Typography component="h1" className="about-title">
            About Celsius
          </Typography>
          <Typography className="about-lead">
            Celsius builds customized industrial refrigeration systems for
            temperature, humidity, and CO₂ control across cold storage and
            industrial applications.
          </Typography>
        </section>

        <section className="about-values">
          {VALUES.map((v) => (
            <div className="about-value-card product-surface-card" key={v.title}>
              <span className="about-value-icon">{v.icon}</span>
              <h3 className="about-value-title">{v.title}</h3>
              <p className="about-value-text">{v.text}</p>
            </div>
          ))}
        </section>

        <section className="about-products">
          <span className="about-overline">What we build</span>
          <Typography component="h2" className="about-section-title">
            Our products
          </Typography>
          <div className="about-products-grid">
            {PRODUCTS.map((p) => (
              <div className="about-product-card" key={p.name}>
                <span className="about-product-name">{p.name}</span>
                <span className="about-product-desc">{p.desc}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="about-cta product-surface-card">
          <Typography component="h2" className="about-cta-title">
            Have a project in mind?
          </Typography>
          <Typography className="about-cta-text">
            Tell us about your cooling requirement and we'll engineer a system
            around it.
          </Typography>
          <button
            type="button"
            className="about-cta-btn"
            onClick={() => navigate("/contactus")}
          >
            Contact us
          </button>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default About;
